import { z } from 'zod';
import { errorCommonI18n } from '@_/i18n-common';
import { LanguageCodes } from '@_/static';

export const i18nObjectSchema = (options?: {
  granularErrors?: boolean;
  maxLength?: number;
  requireAll?: boolean;
  requireOne?: boolean;
  requiredLanguages?: string[];
}) => {
  return z
    .object(
      Object.fromEntries(LanguageCodes.map(language => [language, z.string().trim().nullish()]))
    )
    .superRefine((value, ctx) => {
      const emptyLanguages = Array.from(LanguageCodes.values()).filter(
        language => !value[language]?.trim()
      );

      if (options?.requireAll && emptyLanguages.length < LanguageCodes.length) {
        if (options?.granularErrors) {
          emptyLanguages.forEach(language => pushRequiredError(ctx, language));
        } else {
          pushRequiredError(ctx);
        }
        return z.NEVER;
      }

      if (options?.requiredLanguages?.length) {
        const emptyRequiredLanguages = emptyLanguages.filter(language =>
          options.requiredLanguages?.includes(language)
        );

        if (options?.granularErrors) {
          emptyRequiredLanguages.forEach(language => pushRequiredError(ctx, language));
        } else {
          pushRequiredError(ctx);
        }

        return z.NEVER;
      }

      if (options?.requireOne && emptyLanguages.length >= LanguageCodes.length) {
        if (options?.granularErrors) {
          LanguageCodes.forEach(language => pushRequiredError(ctx, language));
        } else {
          pushRequiredError(ctx);
        }
        return z.NEVER;
      }

      if (options?.maxLength) {
        const overflow = Object.entries(value).find(([, message]) =>
          message ? message.length > options!.maxLength! : false
        );

        if (overflow) {
          const [, message] = overflow;

          ctx.issues.push({
            code: 'too_big',
            inclusive: false,
            input: message,
            maximum: options.maxLength,
            origin: 'string',
            path: []
          });
        }
      }
    });
};

function pushRequiredError(ctx: z.RefinementCtx, language?: string) {
  ctx.issues.push({
    code: 'custom',
    input: '',
    params: {
      i18nMessage: errorCommonI18n.required
    },
    path: language ? [language] : []
  });
}
